import { Dialog, Box, Grid, Typography, Button, ButtonGroup } from '@mui/material';
import React, { useState, useEffect } from "react";
import { useLoading, useAuth, useSnackbar, useSoftwareVersion } from '../Context/Contexts';
import logo from "../assets/Bex_vit2.png";
import InitialSetupWizard from "../components/Wizards/InitialSetupWizard";
import { useTranslation } from "react-i18next";
import OrderCountGraph from "../components/Graphs/OrderCountGraph";
import TopItemGraph from "../components/Graphs/TopItemGraph";
import CountryGraph from "../components/Graphs/CountryGraph";
import ProgramGraph from "../components/Graphs/ProgramGraph";
import ShopAvgGraph from "../components/Graphs/ShopAvgGraph";
import ShopAvgValueGraph from "../components/Graphs/ShopAvgValueGraph";
import ShopReturnFreqGraph from "../components/Graphs/ShopReturnFreqGraph";
import ProductGroupGraph from "../components/Graphs/ProductGroupGraph";
import ItemCategoryGraph from "../components/Graphs/ItemCategoryGraph";
import ItemGroupGraph from "../components/Graphs/ItemGroupGraph";
import DimensionGraph from "../components/Graphs/DimensionGraph";
import GraphBuilder from "../components/Graphs/GraphBuilder";



interface GraphCache {
    graphs: string[];
    ts: number;
}
const HomePage: React.FC = () => {
    const [wizardOpen, setWizardOpen] = useState(false);
    const { showLoading, hideLoading } = useLoading();
    const { isLoading } = useLoading();
    const { authService, isAuthenticated } = useAuth();
    const { showSnackbar } = useSnackbar();
    const { t } = useTranslation();
    const { version, isFetching } = useSoftwareVersion();
    const [builderOpen, setBuilderOpen] = useState(false);
    const graphOptions = [
        { key: "order", label: t("graph.orderGraphTitle") },
        { key: "items", label: t("graph.itemGraphTitle") },
        { key: "program", label: t("graph.programGraphTitle") },
        { key: "country", label: t("graph.countryGraphTitle") },
        { key: "shopAvg", label: t("graph.shopAvgGraphTitle") },
        { key: "shopAvgValue", label: t("graph.shopAvgValueGraphTitle") },
        { key: "shopReturnFreq", label: t("graph.shopReturnFreqGraphTitle") },
        { key: "productGroup", label: t("graph.productGroupGraphTitle") },
        { key: "itemCategory", label: t("graph.itemCategoryGraphTitle") },
        { key: "itemGroup", label: t("graph.itemGroupGraphTitle") },
        { key: "dimension", label: t("graph.dimensionGraphTitle") },
    ];
    const [graphs, setGraphs] = useState<string[]>([]);

    useEffect(() => {
        if (isAuthenticated) {
            const cached = loadGraphCache();
            if (cached) {
                setGraphs(cached);
            } else {
                try {
                    authService
                        .get<{ graphs: string[] | null }>("/graphApi/GetGraphSetting")
                        .then(({ graphs }) => {
                            const list = (graphs && graphs.length > 0) ? graphs : ["order", "items"];
                            setGraphs(list);
                            saveGraphCache(list);
                        })
                        .catch((error: any) => {
                            console.log("grahp error", error);
                        });
                }

                catch (error: any) {
                    showSnackbar(`${t("common.failedToFetchOrders")} : ${error.response?.data ?? error.message}`, "error");
                }
                finally {
                    hideLoading();
                }
            }
        }
    }, [authService, isAuthenticated]);

    const handleSaveGraphs = async (g: string[]) => {
        setGraphs(g);
        saveGraphCache(g);
        showLoading();
        try {
            await authService.post("/graphApi/SaveGraphSetting", { selected: g })
        }
        catch (error: any) {
            showSnackbar(`${t("common.failedToFetchOrders")} : ${error.response?.data ?? error.message}`, "error");
        }
        finally {
            hideLoading();
        }
    };




    const cancelWizard = async () => {
        showLoading();
        setWizardOpen(false);
        try {
            await authService.get("/companyApi/InitialSetupSkip");
            setWizardOpen(false);
            hideLoading();
        }
        catch (error: any) {
            showSnackbar(`Failed to save : ${error.response?.data ?? error.message}`, "error");
        } finally {
            setWizardOpen(false);
            hideLoading();
        }

    }




    useEffect(() => {
        if (isAuthenticated) {
            authService
                .get<{ initialSetup: boolean }>(`/companyApi/InitialSetupRequired`)
                .then(data => setWizardOpen(data.initialSetup))
                .catch(() => console.log("Failed to fetch initial Setup"));
        }
    }, [authService]);


    const GRAPH_CACHE_KEY = "bex_mini_dashboardGraphs";
    const CACHE_MINUTES = 10;



    function saveGraphCache(graphs: string[]) {
        const payload: GraphCache = {
            graphs,
            ts: Date.now(),
        };
        localStorage.setItem(GRAPH_CACHE_KEY, JSON.stringify(payload));
    }
    function loadGraphCache(): string[] | null {
        const raw = localStorage.getItem(GRAPH_CACHE_KEY);
        if (!raw) return null;

        try {
            const cache: GraphCache = JSON.parse(raw);
            const age = (Date.now() - cache.ts) / 60000;
            if (age > CACHE_MINUTES) {
                localStorage.removeItem(GRAPH_CACHE_KEY);
                return null;
            }
            return cache.graphs;
        } catch {
            //kill if parse fails
            localStorage.removeItem(GRAPH_CACHE_KEY);
            return null;
        }
    }

    return (
        <>
            {(!isLoading && isAuthenticated) && (
                <>
                    <img src={logo} alt="Bex Logo" style={{ width: 150 }} />
                    <Box
                        sx={{
                            textAlign: "center",
                            fontSize: "0.8rem",
                            color: "text.secondary",
                            opacity: 0.7,
                        }}
                    >
                        {isFetching ? t("common.loading") : version ? `v${version}` : ""}
                    </Box>
                    <Typography variant="h2" gutterBottom sx={{
                        display: "flex",
                        justifyContent: "center",
                        textAlign: "center"
                    }}>{t("pageLanding.welcome")}</Typography>
                    <Dialog open={wizardOpen} maxWidth="md" fullWidth>
                        <Box p={3}>
                            <InitialSetupWizard cancelWizard={cancelWizard} />
                        </Box>
                    </Dialog>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                        <Button variant="outlined" onClick={() => setBuilderOpen(true)}>{t('graphBuilder.editDashboard')}</Button>
                    </Box>
                    <Grid container spacing={2} sx={{ mt: 2 }}>
                        {graphs.map((g, idx) => {
                            const key = `${g}-${idx}`;
                            if (g === 'order') return (<Grid item xs={12} key={key}><OrderCountGraph /></Grid>);
                            if (g === 'items') return (<Grid item xs={12} key={key}><TopItemGraph /></Grid>);
                            if (g === 'program') return (<Grid item xs={12} key={key}><ProgramGraph /></Grid>);
                            if (g === 'country') return (<Grid item xs={12} key={key}><CountryGraph /></Grid>);
                            if (g === 'shopAvg') return (<Grid item xs={12} key={key}><ShopAvgGraph /></Grid>);
                            if (g === 'shopAvgValue') return (<Grid item xs={12} key={key}><ShopAvgValueGraph /></Grid>);
                            if (g === 'shopReturnFreq') return (<Grid item xs={12} key={key}><ShopReturnFreqGraph /></Grid>);
                            if (g === 'productGroup') return (<Grid item xs={12} key={key}><ProductGroupGraph /></Grid>);
                            if (g === 'itemCategory') return (<Grid item xs={12} key={key}><ItemCategoryGraph /></Grid>);
                            if (g === 'itemGroup') return (<Grid item xs={12} key={key}><ItemGroupGraph /></Grid>);
                            if (g === 'dimension') return (<Grid item xs={12} key={key}><DimensionGraph /></Grid>);
                            return null;
                        })}
                    </Grid>
                    <GraphBuilder
                        open={builderOpen}
                        onClose={() => setBuilderOpen(false)}
                        onSave={handleSaveGraphs}
                        available={graphOptions}
                        selected={graphs}
                    />







                </>
            )}
        </>
    );
}

export default HomePage;
